import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WidgetGrid from '../../../components/layout/widgets/WidgetGrid';
import JarvisWidget from '../../../components/layout/widgets/JarvisWidget';
import { connect } from 'react-redux';
import { deleteCommentList, getComment } from '../../../common/actions/retailer';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import imageUrl from '../../../common/utils/imageUrl';
import { API_ROOT } from '../../../common/constants';
import defaultDiamondImage from '../../../assets/images/image-coming-soon.jpg';
import statusCodeUtil from '../../../common/utils/statusCode';

const API_BASE = API_ROOT.replace(/\/api\/v1\/?$/, '');

const normalizeCommentImageUrl = rawPath => {
  if (!rawPath || typeof rawPath !== 'string') return '';
  const path = rawPath.trim();
  if (!path) return '';

  if (/^data:/i.test(path) || /^https?:\/\//i.test(path)) {
    return path;
  }

  if (/^\/\//.test(path)) {
    return `https:${path}`;
  }

  if (/^\/storage\//i.test(path)) {
    return `${API_BASE}${path}`;
  }

  if (/^storage\//i.test(path)) {
    return `${API_BASE}/${path}`;
  }

  if (/^comments\//i.test(path)) {
    return `${API_BASE}/storage/${path}`;
  }

  if (/^images\//i.test(path)) {
    return `${API_BASE}/${path}`;
  }

  return `${API_BASE}/${path.replace(/^\/+/, '')}`;
};

const parseComments = res =>
  Object.values((res.data && res.data.items) || {}).map(comment => ({
    ...comment,
    imageUrl: normalizeCommentImageUrl(comment.imageUrl || comment.image_url || ''),
  }));

const isImagePath = path => /\.(jpe?g|png|gif|webp|bmp|svg)(\?.*)?$/i.test(path);

const getApiErrorMessage = error => {
  if (!error) return 'An unexpected error occurred.'
  const status = error.response && error.response.status
  const data = error.response && error.response.data
  if (status === 401) return 'Your session has expired. Please log in again.'
  if (status === 422) {
    if (data && data.errors && typeof Object.values(data.errors)[0] === 'string') {
      return Object.values(data.errors).join(' ')
    }
    if (data && data.message) return data.message
    return 'Invalid request. Please try again.'
  }
  if (status === 500) return 'Server error. Please try again later.'
  if (status === 400) return 'Bad request. Please check your input and try again.'
  if (data && data.message) return data.message
  return error.message || 'An unexpected error occurred.'
}

function CommenList({ Comments, DeleteComments }) {
  const [allComments, setAllComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productInfo, setProductInfo] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Read product info passed from the listing page
    try {
      const stored = sessionStorage.getItem('commentProductInfo')
      if (stored) setProductInfo(JSON.parse(stored))
    } catch (e) {}

    setLoading(true);
    Comments(id)
      .then(res => {
        setAllComments(parseComments(res));
        setLoading(false);
      })
      .catch(() => {
        setAllComments([]);
        setLoading(false);
      });
  }, [id]);

  const handleDeleteComment = commentId => {
    toast(
      ({ closeToast }) => (
        <div>
          <p>Are you sure you want to delete this comment?</p>
          <button
            style={{ marginRight: '10px' }}
            className='btn btn-primary mx-2'
            onClick={() => {
              deleteComment(commentId);
              closeToast();
            }}
          >
            Yes
          </button>
          <button className='btn btn-danger mx-2' onClick={closeToast}>No</button>
        </div>
      ),
      { position: toast.POSITION.TOP_CENTER, autoClose: false, closeOnClick: false, draggable: false }
    );
  };

  const deleteComment = commentId => {
    const data = { comment_id: commentId };
    DeleteComments(data)
      .then(() => {
        Comments(id)
          .then(res => {
            setAllComments(parseComments(res));
            toast.success('Comment deleted successfully!');
          })
          .catch(() => setAllComments([]));
      })
      .catch(error => {
        toast.error(getApiErrorMessage(error));
      });
  };

  const commentsArray = Array.isArray(allComments) ? allComments : [];

  const renderProductInfoBanner = () => {
    if (!productInfo) return null;
    const { customTitle, internalId, imageUrl: imgUrl, price, setting, statusCode, retailerName } = productInfo;
    const productImage = imgUrl
      ? imageUrl('inventory', imgUrl)
      : defaultDiamondImage;
    return (
      <div style={{ padding: '15px 20px', borderBottom: '1px solid #e5e5e5', background: '#f9f9f9', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
        <img
          src={productImage}
          onError={e => { e.target.onerror = null; e.target.src = defaultDiamondImage; }}
          alt="Product"
          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
        />
        <div style={{ flex: 1, minWidth: 200 }}>
          {/* {customTitle && <div><strong>Product Title:</strong> {customTitle}</div>} */}
          {internalId && <div><strong>Internal ID:</strong> {internalId}</div>}
          {statusCode && <div><strong>Status:</strong> {statusCode ? statusCodeUtil.diamond.statusCodeToText(statusCode).replace(/^\w/, c => c.toUpperCase()) : ''}</div>}
          {price && <div><strong>Price:</strong> {price}</div>}
          {setting && <div><strong>Setting:</strong> {setting}</div>}
          {retailerName && <div><strong>Retailer:</strong> {retailerName}</div>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <ToastContainer />
      <div id="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12">
              <JarvisWidget editbutton={false} togglebutton={false} fullscreenbutton={false} deletebutton={false} color="darken">
                <header>
                  <span className="widget-icon"><i className="fa fa-list" /></span>
                  <h2>Product Comments</h2>
                  <div className="widget-toolbar">
                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      onClick={() => navigate('/commentingSystem')}
                    >
                      <i className="fa fa-arrow-left" /> Back
                    </button>
                  </div>
                </header>
                <div className='produtereviewdetails'>
                  {renderProductInfoBanner()}
                  <div className="widget-body no-padding listdetails_view">
                    <div className='prod_add'>
                      <div className='prod_add_review_option'>
                        <ul className='allreviewlist'>
                          {commentsArray && commentsArray.length > 0 ? (
                            commentsArray.map(comment => (
                              <li key={comment.id}>
                                <div className='reviewinfo'>
                                  <h4>{`${comment.username}(Email - ${comment.email})`}</h4>
                                  <p><small>Posted on: {new Date(comment.datetime * 1000).toLocaleDateString()}</small></p>
                                  <p>{comment.content}</p>
                                  {comment.imageUrl && (
                                    <p>
                                      {isImagePath(comment.imageUrl) || /^data:image\//i.test(comment.imageUrl) ? (
                                        <img
                                          src={comment.imageUrl}
                                          alt='comment image'
                                          width={'auto'}
                                          height={60}
                                          onError={e => {
                                            e.target.onerror = null;
                                            e.target.src = defaultDiamondImage;
                                          }}
                                        />
                                      ) : (
                                        <a href={comment.imageUrl} target='_blank' rel='noopener noreferrer'>
                                          View attachment
                                        </a>
                                      )}
                                    </p>
                                  )}
                                </div>
                                <div className='trashopt2'>
                                  <span className='deleateicon'>
                                    <a onClick={() => handleDeleteComment(comment.id)} className='btn btn-danger'>
                                      <i className="fa fa-lg fa-trash"></i>
                                    </a>
                                  </span>
                                </div>
                              </li>
                            ))
                          ) : (
                            <li style={{ textAlign: 'center', width: '100%', display: 'block', fontSize: '24px', color: '#40cba4' }}>
                              {loading ? (
                                <span><i className="fa fa-spinner fa-spin" /> Loading...</span>
                              ) : (
                                'Comment Not Found'
                              )}
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    </div>
  );
}

const mapStateToProps = () => ({});

const mapDispatchToProps = dispatch => ({
  Comments: id => dispatch(getComment(id)),
  DeleteComments: id => dispatch(deleteCommentList(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(CommenList);
